import type {
  AuthenticationResult,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { persistor, store } from "../store/store";
import {
  clearAccessToken,
  setAccessToken,
} from "../store/modules/auth/authSlice";
import {
  StorageKeys,
  clearBrowserStorage,
  readStorage,
  writeStorage,
} from "./browserStorage";

export type AuthMethod = "native" | "redirect";

let sessionVersion = 0;
let signOutInFlight = false;
const listeners = new Set<() => void>();

/**
 * For useSyncExternalStore. Native sign-in raises no MSAL login events, so anything that decides
 * between signed-in and signed-out UI subscribes here as well.
 */
export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSessionVersion(): number {
  return sessionVersion;
}

export function notifySessionChanged(): void {
  sessionVersion += 1;
  listeners.forEach((listener) => listener());
}

export function secondsUntil(expiresOn: Date | null | undefined): number {
  return expiresOn
    ? Math.max(0, Math.floor((expiresOn.getTime() - Date.now()) / 1000))
    : 3600;
}

/** Makes a token result the app session: the active MSAL account plus the API token in Redux. */
export function applyAuthResult(
  pca: IPublicClientApplication,
  result: AuthenticationResult,
  method?: AuthMethod
): void {
  if (result.account) {
    pca.setActiveAccount(result.account);
  }

  store.dispatch(
    setAccessToken({
      token: result.accessToken,
      expiresIn: secondsUntil(result.expiresOn),
    })
  );

  if (method) {
    writeStorage("local", StorageKeys.authMethod, method);
  }

  notifySessionChanged();
}

export function getAuthMethod(): AuthMethod | null {
  const method = readStorage("local", StorageKeys.authMethod);
  return method === "native" || method === "redirect" ? method : null;
}

/**
 * True from the moment sign-out starts until the browser leaves the page.
 *
 * Clearing the session re-renders the app as signed out while it is still on an app route, and the
 * signed-out catch-all route starts a new hosted sign-in. Entra's session cookie would then sign the
 * user straight back in, landing them where they started — so the UI waits instead.
 */
export function isSigningOut(): boolean {
  return signOutInFlight;
}

export function beginSignOut(): void {
  signOutInFlight = true;
  notifySessionChanged();
}

/**
 * Clears Redux, persisted state and browser storage (keeping only the affiliate slug).
 *
 * `keepTokenCache` leaves Web Storage alone, which is where MSAL keeps its token cache. Hosted
 * sign-out needs it: `logoutRedirect` reads the id token to sign the user out without asking which
 * account they meant. The /logged-out page clears whatever is left.
 */
export async function clearLocalSession(
  options: { keepTokenCache?: boolean } = {}
): Promise<void> {
  store.dispatch(clearAccessToken());

  try {
    await persistor.purge();
    await persistor.flush();
  } catch (error) {
    console.error("Failed to purge persisted state", error);
  }

  if (!options.keepTokenCache) {
    clearBrowserStorage();
  }
  notifySessionChanged();
}
